import { TruckShippingIcon } from 'src/components/Icons/TruckShippingIcon'
import { CloseButtonIcon } from 'src/components/Icons/CloseButtonIcon'
import type { ShippingCampaignEvaluation } from 'src/utils/shippingCampaign'

type ShippingConditionsModalProps = {
  title: string
  subtitle: string
  rules: Array<ShippingCampaignEvaluation['modal']['rules'][number] | string>
  footer: string
  onClose: () => void
}

type NormalizedRule = {
  id: string
  regionLabel: string
  connectorText: string
  priceLabel: string
}

function normalizeRule(
  rule: ShippingCampaignEvaluation['modal']['rules'][number] | string,
  index: number
): NormalizedRule {
  if (typeof rule === 'string') {
    const [regionLabel, details = ''] = rule.split(' — ')
    const priceMatch = details.match(/R\$\s?[\d.,]+/)
    const [priceLabel] = priceMatch ?? ['']

    return {
      id: `legacy-rule-${index}`,
      regionLabel,
      connectorText: details.replace(priceLabel, '').trim(),
      priceLabel,
    }
  }

  if (rule && typeof rule === 'object') {
    const typedRule = rule as {
      id?: string
      regionLabel?: string
      connectorText?: string
      priceLabel?: string
    }

    return {
      id: typedRule.id ?? `rule-${index}`,
      regionLabel: typedRule.regionLabel ?? '',
      connectorText: typedRule.connectorText ?? '',
      priceLabel: typedRule.priceLabel ?? '',
    }
  }

  return {
    id: `rule-${index}`,
    regionLabel: '',
    connectorText: '',
    priceLabel: '',
  }
}

export function ShippingConditionsModal({
  title,
  subtitle,
  rules,
  footer,
  onClose,
}: Readonly<ShippingConditionsModalProps>) {
  const normalizedRules = rules.map((rule, index) => normalizeRule(rule, index))

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="shipping-conditions-modal-title"
      className="relative w-full md:max-w-[425px] rounded-lg bg-white p-8 shadow-2xl"
    >
      <div className="absolute right-4 top-4 flex items-center justify-end">
        <button
          type="button"
          onClick={onClose}
          aria-label="Fechar"
          className="rounded-full p-1"
        >
          <CloseButtonIcon size={20} />
        </button>
      </div>

      <div className="mb-4 mr-auto flex h-12 w-12 items-center justify-center rounded-full bg-shippingCampaignSoftSuccess">
        <TruckShippingIcon color="#00875A" size={24} />
      </div>

      <h2
        id="shipping-conditions-modal-title"
        className="mb-1 text-left text-lg font-bold leading-[1.3] text-shippingCampaignTitle"
      >
        {title}
      </h2>

      <p className="mb-4 text-sm leading-[1.5] text-shippingCampaignSubtitle">
        {subtitle}
      </p>

      <ul className="mb-4 flex flex-col gap-2.5 rounded bg-[#f5f5f5] p-4">
        {normalizedRules.map((rule) => (
          <li
            key={rule.id}
            className="text-[13px] leading-[1.5] text-shippingCampaignRule before:mt-[5px] before:mr-2 before:inline-block before:h-2 before:w-2 before:shrink-0 before:rounded-full before:bg-shippingCampaignSuccess before:content-['']"
          >
            <span className="font-bold">{rule.regionLabel} —</span>
            <span> {rule.connectorText} </span>
            <span className="font-bold">{rule.priceLabel}</span>
          </li>
        ))}
      </ul>

      <p className="border-shippingCampaignDivider border-t pt-4 text-[11px] leading-[1.6] text-graysafe">
        {footer}
      </p>
    </div>
  )
}
